import React, { useEffect, useMemo, useRef, useState } from "react";
import { Task } from "@/bryntum-lib/models/Task";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { taskSchema, TaskFormData } from "@/react/schema/taskSchema";
import { ColorPicker } from "@/modern/components/color-picker";
import { eventStyles, STRIPED_TASK_STYLE } from "@/other/constants/TaskTypes";
import { TaskStylePreview } from "@/react/components/entityViews/contextMenus/task/TaskStylePreview";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getNewTaskColor } from "@/other/utils/bgUtils";
import TaskEditorColorMapping from "@/other/constants/TaskEditorColors";
import type { ISwivuStore } from "@/types/SwivuStore";
import { toast } from "sonner";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { SwivuDuplicateEntityHelper } from "@/react/components/SwivuDuplicateEntityHelper";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { useConfirm } from "@omit/react-confirm-dialog";
import { EditorFormField } from "../components/EditorFormField";
import { EditorSection } from "../components/EditorSection";
import { EditorAuditFooter } from "../components/EditorAuditFooter";
import { formatAuditDate, type AuditItem } from "../utils/editorUtils";
import { EditorMetaDetails } from "../components/EditorMetaDetails";
import { EDITOR_META_ITEMS } from "../constants/editorMetaItems";
import AddressAutoComplete from "../../location/components/AddressAutoComplete";
import { Location } from "@/bryntum-lib/models/Location";
import { type GooglePlaceDetails } from "@/react/hooks/entityHooks/useSwivuLocation";
import { useWarRoom } from "@/react/hooks/useWarRoom";
import { EditorWarRoomToggle } from "../components/EditorWarRoomToggle";

interface TaskGeneralEditorProps {
  record: {
    task: Task;
    onAfterRecordAdd?: (taskRecord: Task) => void;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const getRecentTaskByLimit = (store: ISwivuStore<Task>, limit = 6): Task[] => {
  return store
    .query(() => true, true)
    .sort((a: Task, b: Task) => {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    })
    .slice(0, limit);
};

const allStyles = [...eventStyles, STRIPED_TASK_STYLE];

const taskMetaItems = EDITOR_META_ITEMS[ENTITY_TYPES.TASK];

export const TaskGeneralEditor = ({ record, onClose, editorInstance }: TaskGeneralEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const { project, entityManager } = useSwivuBryntumContext();
  const { isWarRoom, toggleWarRoom } = useWarRoom("task-editor");
  const { task: taskRecord, onAfterRecordAdd } = record;
  const { subscribe } = useSwivuStore<Task>({ storeId: STORE_KEYS.taskStore });
  const confirm = useConfirm();

  /**
   * Holds a phantom Location record created during this editor session.
   * It is added to locationStore only when the task is saved.
   */
  const locationRecordRef = useRef<Location | null>(null);

  /** Controlled address string shown in the AddressAutoComplete input. */
  const [locationAddress, setLocationAddress] = useState<string>(
    taskRecord.location?.streetAddress ?? ""
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: taskRecord.name || "",
      agendaTitle: taskRecord.agendaTitle || "",
      description: taskRecord.description || "",
      color: taskRecord.color || "#FF8A65",
      taskStyle: taskRecord.taskStyle || eventStyles[0],
      statusId: taskRecord.statusId || null,
      categoryId: taskRecord.categoryId || null,
      teamId: taskRecord.teamId || null,
      phaseId: taskRecord.phaseId || null,
      tagsId: taskRecord.tagsId || null,
      clientId: taskRecord.clientId || null,
      budgetId: taskRecord.budgetId || null,
      locationId: taskRecord.locationId ?? null,
    },
  });

  const currentColor = watch("color");
  const currentStyle = watch("taskStyle");

  useEffect(() => {
    if (taskRecord.isPhantom) {
      const recentCreatedTasks = getRecentTaskByLimit(project.extraTaskStore);
      const { color } = getNewTaskColor(TaskEditorColorMapping, recentCreatedTasks.map((r) => r.color));
      reset({ ...getValues(), color });
    }
    return subscribe(({ record: changedRecord }) => {
      if (taskRecord.id === changedRecord.id) {
        reset({
          name: changedRecord.name || "",
          agendaTitle: changedRecord.agendaTitle || "",
          description: changedRecord.description || "",
          color: changedRecord.color || "#FF8A65",
          taskStyle: changedRecord.taskStyle || eventStyles[0],
          statusId: changedRecord.statusId || null,
          categoryId: changedRecord.categoryId || null,
          teamId: changedRecord.teamId || null,
          phaseId: changedRecord.phaseId || null,
          tagsId: changedRecord.tagsId || null,
          clientId: changedRecord.clientId || null,
          budgetId: changedRecord.budgetId || null,
          locationId: changedRecord.locationId ?? null,
        });
        return true;
      }
      return false;
    });
  }, [taskRecord]);

  // ─── AddressAutoComplete callbacks ──────────────────────────────────────────

  const handleSelectSavedLocation = (loc: Location) => {
    locationRecordRef.current = null;
    setLocationAddress(loc.streetAddress ?? "");
    setValue("locationId", loc.id as number | string, { shouldDirty: true });
  };

  const handleSelectGooglePlace = (details: GooglePlaceDetails) => {
    const phantom = project.locationStore.createRecord({
      streetAddress: details.streetAddress ?? details.formattedAddress,
      city: details.city ?? "",
      stateOrRegion: details.stateOrRegion ?? "",
      postalCode: details.postalCode ?? "",
      country: details.country ?? "",
      latitude: details.lat || null,
      longitude: details.lon || null,
    }) as unknown as Location;

    locationRecordRef.current = phantom;
    setLocationAddress(details.streetAddress ?? details.formattedAddress);
    setValue("locationId", phantom.id as number | string, { shouldDirty: true });
  };

  const handleSetManualLocation = (address: string) => {
    const phantom = project.locationStore.createRecord({
      streetAddress: address,
    }) as unknown as Location;

    locationRecordRef.current = phantom;
    setLocationAddress(address);
    setValue("locationId", phantom.id as number | string, { shouldDirty: true });
  };

  // ─── Save ────────────────────────────────────────────────────────────────────

  const onSubmit = async (data: TaskFormData) => {
    try {
      setIsSaving(true);

      if (locationRecordRef.current) {
        const phantomLoc = locationRecordRef.current;
        locationRecordRef.current = null;

        // Phase 1: persist the phantom location so the server assigns a real numeric ID.
        // After this sync, phantomLoc.id is a real number — no type-coercion issue when
        // setting it on the task's locationId field (which is typed as "number" in Bryntum).
        project.locationStore.add(phantomLoc);
        await project.sync();

        // Phase 2: set the task fields and link the now-numeric locationId
        const { locationId: _ignored, ...restData } = data;
        taskRecord.set(restData);
        taskRecord.set("locationId", phantomLoc.id);
      } else {
        // No phantom — data.locationId is already a real number or null, safe to bulk-set
        taskRecord.set(data);
      }

      if (taskRecord.isPhantom) {
        project.extraTaskStore.add(taskRecord);
        onAfterRecordAdd?.(taskRecord);
      }
      await project.sync();
      toast.success("Task saved successfully");
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Something went wrong while saving the task. Please try again.", {
        description: "If the problem persists, please contact support.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => handleSubmit(onSubmit, (errors) => {
    console.log("errors", errors);
  })();

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
        alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">Keep working</Button>
            <Button variant="destructive" onClick={confirm} size="sm">Do not save</Button>
            <Button variant="success" size="sm" onClick={async () => {
              if (!isValid) { await trigger("name"); cancel(); return; }
              handleSave(); confirm();
            }}>Save & close</Button>
          </div>
        ),
      });
      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  const openDashboard = () => {
    if (entityManager) {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id });
    }
  };

  const handleSaveAndDashboard = async () => {
    await handleSave();
    if (isValid) { openDashboard(); onClose(); }
  };

  const hideDashboardButton = entityManager.isOpen && entityManager.entityId == taskRecord.id;

  const auditItems = useMemo<AuditItem[]>(() => {
    const items: AuditItem[] = [];
    if (taskRecord.creatorRecord?.name && taskRecord.createdAt)
      items.push({ key: "created", label: "Created", value: `${taskRecord.creatorRecord.name} - ${formatAuditDate(taskRecord.createdAt)}` });
    if (taskRecord.updaterRecord?.name && taskRecord.updatedAt)
      items.push({ key: "updated", label: "Updated", value: `${taskRecord.updaterRecord.name} - ${formatAuditDate(taskRecord.updatedAt)}` });
    if (taskRecord.lastEventUpdatedByRecord?.name && taskRecord.lastEventUpdatedAt)
      items.push({ key: "scheduled", label: "Scheduled", value: `${taskRecord.lastEventUpdatedByRecord.name} - ${formatAuditDate(taskRecord.lastEventUpdatedAt)}` });
    return items;
  }, [taskRecord.createdAt, taskRecord.creatorRecord?.name, taskRecord.updatedAt, taskRecord.updaterRecord?.name, taskRecord.lastEventUpdatedAt, taskRecord.lastEventUpdatedByRecord?.name]);

  const headerRenderer = () => (
    <div
      className="drag-handle flex items-center gap-4 p-5 border-b border-border/50 rounded-t-lg border-t-2"
      style={{ borderTopColor: currentColor }}
    >
      <h2 className="text-xl font-bold tracking-tight text-foreground">General Settings</h2>
      <p className="text-sm text-muted-foreground">Tasks can be jobs, projects, assignments, actions, goals, chores, lessons, responsibilities, activities, and more.</p>
      <div className="ml-auto">
        <EditorWarRoomToggle pressed={isWarRoom} onPressedChange={toggleWarRoom} />
      </div>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={taskRecord}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{ width: "66rem", height: "min(48rem, 90vh)", maxHeight: "90vh", maxWidth: "90vw", minWidth: "66rem", minHeight: "min(48rem, 90vh)" }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      footerRenderer={() => (
        <div className="w-full">
          <div className="flex w-full flex-col gap-3 py-2 md:flex-row md:items-center md:justify-between">
            <EditorAuditFooter items={auditItems} isSaving={isSaving} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button onClick={handleSave} disabled={(!taskRecord.isPhantom && !isDirty) || isSaving}>Save Changes</Button>
              {!hideDashboardButton && (
                <Button
                  type="button"
                  disabled={isSaving}
                  onClick={() => {
                    if (!isDirty && !taskRecord.isPhantom) { openDashboard(); onClose(); }
                    else { handleSaveAndDashboard(); }
                  }}
                >
                  Open Dashboard <i className="fa-solid fa-arrow-right -rotate-45" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      headerRenderer={headerRenderer}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible ${editorInstance?.className || ""}`}
      style={{ zoom: isWarRoom ? 1.15 : 1 }}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Section 1 — Identity */}
            <EditorSection>
              {/* Task Name */}
              <EditorFormField label="Task Name" required error={errors.name?.message}>
                <Controller
                  name="name"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => {
                    const DuplicateHelperComponent = SwivuDuplicateEntityHelper as React.ComponentType<{
                      record: Task; store: ISwivuStore<Task>; placeholder: string; editorReactState: unknown;
                      onChange: (params: { value: string; record?: Task }) => void; disabled: boolean;
                      editorRecordKey: string; inputElementClassName: string; name: string; value: string; onBlur: () => void;
                    }>;
                    return (
                      <DuplicateHelperComponent
                        {...field}
                        record={taskRecord}
                        store={project.extraTaskStore}
                        placeholder="Displayed on HQ and Workforce"
                        editorReactState={editorInstance}
                        onChange={({ value, record: selectedRecord }: { value: string; record?: Task }) => {
                          if (selectedRecord) {
                            reset({
                              name: selectedRecord.name || "", agendaTitle: selectedRecord.agendaTitle || "",
                              description: selectedRecord.description || "", color: selectedRecord.color || "#FF8A65",
                              taskStyle: selectedRecord.taskStyle || eventStyles[0], statusId: selectedRecord.statusId || null,
                              categoryId: selectedRecord.categoryId || null, teamId: selectedRecord.teamId || null,
                              phaseId: selectedRecord.phaseId || null, tagsId: selectedRecord.tagsId || null,
                              clientId: selectedRecord.clientId || null, budgetId: selectedRecord.budgetId || null,
                              locationId: selectedRecord.locationId ?? null,
                            });
                            locationRecordRef.current = null;
                            setLocationAddress(selectedRecord.location?.streetAddress ?? "");
                            if (taskRecord.isPhantom) onAfterRecordAdd?.(selectedRecord);
                          } else {
                            field.onChange(value);
                            if (taskRecord.isPhantom) setValue("agendaTitle", value, { shouldDirty: true });
                          }
                        }}
                        disabled={isSaving}
                        editorRecordKey="task"
                        inputElementClassName="flex w-full rounded-md text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                      />
                    );
                  }}
                />
              </EditorFormField>

              {/* External Name + Location */}
              <div className="grid grid-cols-2 gap-5">
                <EditorFormField label="External Name" htmlFor="agendaTitle" error={errors.agendaTitle?.message}>
                  <Controller
                    name="agendaTitle"
                    control={control}
                    disabled={isSaving}
                    render={({ field }) => (
                      <Input
                        id="agendaTitle"
                        variant="outline"
                        placeholder="Displayed on Client and Public Schedules"
                        autoComplete="off"
                        {...field}
                        className={errors.agendaTitle ? "border-destructive" : ""}
                      />
                    )}
                  />
                </EditorFormField>

                <EditorFormField label="Location">
                  <AddressAutoComplete
                    value={locationAddress}
                    onChange={(addr) => {
                      setLocationAddress(addr);
                      if (!addr) {
                        locationRecordRef.current = null;
                        setValue("locationId", null, { shouldDirty: true });
                      }
                    }}
                    onSelectSavedLocation={handleSelectSavedLocation}
                    onSelectGooglePlace={handleSelectGooglePlace}
                    onSetManual={handleSetManualLocation}
                    placeholder="Search for a location…"
                    disabled={isSaving}
                  />
                </EditorFormField>
              </div>

              {/* Description */}
              <EditorFormField label="Description" htmlFor="description" error={errors.description?.message}>
                <Controller
                  name="description"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Textarea
                      id="description"
                      placeholder="Add a detailed description..."
                      autoComplete="off"
                      {...field}
                      className={`min-h-[100px] !text-base resize-y${errors.description ? " border-destructive" : ""}`}
                    />
                  )}
                />
              </EditorFormField>

              {/* Task Color + Task Style */}
              <div className="grid grid-cols-2 gap-5">
                <EditorFormField label="Task Color">
                  <ColorPicker value={currentColor} onChange={(color) => { if (color) setValue("color", color, { shouldDirty: true }); }} editable={false} className="border-0 shadow-none">
                    <Button disabled={isSaving} type="button" variant="outline" className="w-full justify-start h-swivu-input text-base px-3">
                      <div className="h-4 w-4 rounded border flex-shrink-0" style={{ backgroundColor: currentColor || "transparent" }} />
                      <span className="truncate">{currentColor || "Select"}</span>
                    </Button>
                  </ColorPicker>
                </EditorFormField>

                <EditorFormField label="Task Style">
                  <Popover open={stylePickerOpen} onOpenChange={setStylePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start h-swivu-input text-base px-3" disabled={isSaving}>
                        <i className="fa-light fa-palette" />
                        <span className="truncate">{currentStyle.charAt(0).toUpperCase() + currentStyle.slice(1)}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                      <div className="space-y-1">
                        {allStyles.map((style) => (
                          <button key={style} type="button" disabled={isSaving} onClick={() => { setValue("taskStyle", style, { shouldDirty: true }); setStylePickerOpen(false); }} className="w-full p-2 rounded hover:bg-accent transition-colors">
                            <TaskStylePreview style={style} isSelected={currentStyle === style} color={currentColor} />
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </EditorFormField>
              </div>
            </EditorSection>

            {/* Section 2 — Classification & Tags */}
            <EditorSection withDivider>
              <EditorMetaDetails
                record={taskRecord}
                storeKey={STORE_KEYS.taskStore}
                metaItems={taskMetaItems}
                isDirectUpdates={false}
                onFieldChange={(changes) => setValue(changes.fieldName as keyof TaskFormData, changes.value, { shouldDirty: true })}
                isFieldDisabled={isSaving}
              />
            </EditorSection>

          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
